import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { STEPS } from './steps';

/**
 * Shared wizard card shell: title, supporting line, body, and a single
 * action row. Focus moves to the card title on mount so each step change is
 * announced to screen readers (UX.md accessibility).
 *
 * Wrap field-based steps by passing `onSubmit` — the card renders a <form> so
 * Enter submits the primary action. Action-only steps use `onPrimary`.
 *
 * @param {Object} props
 * @param {string} props.title
 * @param {React.ReactNode} [props.subtitle]
 * @param {React.ReactNode} props.children            - card body
 * @param {string} props.primaryLabel
 * @param {() => void} [props.onPrimary]
 * @param {boolean} [props.primaryDisabled]
 * @param {boolean} [props.primaryLoading]
 * @param {(e: React.FormEvent) => void} [props.onSubmit] - if set, body is a form
 * @param {() => void} [props.onBack]                 - renders quiet "Back" link
 * @param {() => void} [props.onSkip]                 - renders quiet "Skip for now" link
 * @param {React.ReactNode} [props.secondary]         - extra quiet link (e.g. path switch)
 * @param {string} [props.error]                      - inline error above the action row
 */
const WizardCard = ({
  title,
  subtitle,
  children,
  primaryLabel,
  onPrimary,
  primaryDisabled,
  primaryLoading,
  onSubmit,
  onBack,
  onSkip,
  secondary,
  error,
}) => {
  const titleRef = useRef(null);
  const location = useLocation();

  // Progress header is derived from the route so step bodies stay unchanged.
  const idx = STEPS.findIndex((s) => location.pathname.startsWith(s.path));
  const humanStep = idx >= 0 ? idx + 1 : 1;
  const totalSteps = STEPS.length;
  const progressPct = Math.round((humanStep / totalSteps) * 100);

  useEffect(() => {
    // Move focus to the title on each step change.
    titleRef.current?.focus();
  }, [title]);

  const actionRow = (
    <div className="wiz-actions">
      <div className="wiz-actions-quiet">
        {onBack && (
          <button type="button" className="wiz-link" onClick={onBack}>
            Back
          </button>
        )}
        {onSkip && (
          <button type="button" className="wiz-link" onClick={onSkip}>
            Skip for now
          </button>
        )}
      </div>
      <button
        type={onSubmit ? 'submit' : 'button'}
        className="rd-btn rd-btn-primary wiz-primary"
        onClick={onSubmit ? undefined : onPrimary}
        disabled={primaryDisabled || primaryLoading}
      >
        {primaryLoading ? <span className="loading-spinner" /> : primaryLabel}
      </button>
    </div>
  );

  const body = (
    <>
      <div className="ob-progress">
        <div className="ob-progress-meta">
          <span className="ob-progress-step">
            Step {humanStep} of {totalSteps}
          </span>
          <span className="ob-progress-pct">{progressPct}% complete</span>
        </div>
        <div className="ob-progress-track">
          <div className="ob-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      <div className="wiz-card-head">
        <h1 className="wiz-title" tabIndex={-1} ref={titleRef}>
          {title}
        </h1>
        {subtitle && <p className="wiz-subtitle">{subtitle}</p>}
      </div>

      <div className="wiz-card-body">{children}</div>

      {error && (
        <div className="wiz-form-error" role="alert">
          {error}
        </div>
      )}

      {actionRow}

      {secondary && <div className="wiz-secondary">{secondary}</div>}
    </>
  );

  return (
    <section className="wiz-card" aria-labelledby="wiz-title">
      {onSubmit ? <form onSubmit={onSubmit} noValidate>{body}</form> : body}
    </section>
  );
};

export default WizardCard;
